import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Props } from './EditorTopBar';
import DocState from './DocState';
import MobileSheet from '../MobileSheet';
import FeaturesPanel from '../FeaturesPanel';
import StatusToggle from '../StatusToggle';
import { STATUS_PILL_LABELS } from './statusLabels';
import type { Theme } from '../../lib/theme';
import PublishButton from '../Publish/PublishButton';
import { longWhen, shortWhen } from '../../lib/publish';

/** Row one's publish line (2A): which version the client reads. */
function publishLine(p: Props): string | null {
  const z = p.publishZone;
  if (!z) return null;
  if (z.status === 'draft' && z.round !== null) return `The page is down. Last published v${z.round}${z.publishedAt ? ` on ${longWhen(z.publishedAt)}` : ''}.`;
  if (z.justPublished) return `v${z.justPublished.round} · ${z.justPublished.quiet ? 'Updated' : 'Published'} just now`;
  if (z.round === null) return 'Not shared yet';
  if (z.kind !== 'none') return `Unpublished changes · ${z.status === 'approved' ? 'Approved copy is' : 'client reads'} v${z.round}`;
  return z.publishedAt ? `v${z.round} · Published ${shortWhen(z.publishedAt)}` : `v${z.round}`;
}

type Sheet = 'tools' | 'views' | 'feedback' | 'status' | 'share' | null;
export default function MobileEditorNav(p: Props & { theme: Theme; toggleTheme: () => void }) {
  const [sheet, setSheet] = useState<Sheet>(null);
  const [copied, setCopied] = useState(false);
  const { theme, toggleTheme: toggle } = p;
  const action = (fn: (() => void) | undefined) => { setSheet(null); fn?.(); };
  const row = (label: string, fn: () => void, reason?: string) => <button type="button" className="mobile-nav-row" disabled={!!reason} onClick={fn}><span>{label}{reason && <small>{reason}</small>}</span><span aria-hidden>›</span></button>;
  return <>
    <header className="mobile-editor-head" data-print-hide>
      <div className="mobile-editor-identity">
        <Link to="/admin" aria-label="Back to Scripts">‹</Link>
        <div><h1>{p.title}</h1><p>{publishLine(p) ?? `V${p.version} · ${p.kind === 'brief' ? 'Interview brief' : 'Admin workspace'}`}</p></div>
        <button aria-label="Script tools" onClick={() => setSheet('tools')}>⋯</button>
      </div>
      <div className="mobile-view-strip"><span>{p.compareMode ? 'Compare' : 'Editor'}</span>
        <span className="mobile-save-label" aria-hidden>{p.saveState === 'clean' || p.saveState === 'saved' ? 'Saved' : p.saveState === 'error' ? 'Save failed' : p.saveState === 'dirty' ? 'Editing' : p.saveState === 'stale' ? 'Stale' : 'Saving'}</span>
        <DocState state={p.saveState} version={p.version} errorMsg={p.saveErrorMsg} onRetry={p.onRetrySave} clientPresent={p.clientPresent} />
        {p.showPublish && p.onPublish && !p.compareMode && (
          <PublishButton className="mobile-publish" saveFailed={p.saveState === 'error'} busyRound={p.publishBusyRound ?? null} onClick={p.onPublish} />
        )}
        <button onClick={() => setSheet('views')}>Views ▾</button>
      </div>
      {p.compareMode && <button className="mobile-stop-compare" onClick={p.onStopComparing}>Stop comparing</button>}
    </header>
    <nav className="mobile-editor-bottom" aria-label="Editor sections" data-print-hide>
      <button aria-pressed={p.mobilePane !== 'scratch' && !p.mobilePanel && !p.compareMode} onClick={() => { p.onStopComparing(); p.onMobilePane?.('script'); }}>Script</button>
      <button aria-pressed={p.mobilePane === 'scratch' && !p.mobilePanel && !p.compareMode} onClick={() => { p.onStopComparing(); p.onMobilePane?.('scratch'); }}>Scratch</button>
      <button aria-pressed={p.mobilePanel === 'comments' || p.mobilePanel === 'suggestions' || p.compareMode} onClick={() => setSheet('feedback')}>Feedback</button>
      <button ref={p.fieldsButtonRef} onClick={() => setSheet('tools')}>Tools</button>
    </nav>
    <MobileSheet open={sheet !== null} title={sheet === 'views' ? 'Choose your view' : sheet === 'feedback' ? 'Client feedback' : sheet === 'status' ? 'Status & client features' : sheet === 'share' ? 'Share & export' : 'Script tools'} onClose={() => setSheet(null)}>
      {sheet === 'views' && <>
        {row(p.kind === 'brief' ? 'Brief editor' : 'Script editor', () => action(() => { p.onStopComparing(); p.onMobilePane?.('script'); }))}
        {row('Client review', () => action(p.onOpenClientView), p.linkShareable ? undefined : 'Move past Draft to share')}
        {row('Shoot view', () => action(p.onOpenShootView ?? undefined), p.onOpenShootView ? undefined : 'Add script rows, questions or shoot-visible brief fields')}
        {row('Share & export', () => setSheet('share'))}
      </>}
      {sheet === 'feedback' && <>
        {row('Comments & replies', () => action(() => p.onMobilePanel?.('comments')))}
        {row('Suggestions', () => action(() => p.onMobilePanel?.('suggestions')))}
        {row('Client notes', () => action(() => p.onMobilePanel?.('notes')))}
      </>}
      {sheet === 'tools' && <>
        {row('Status & client features', () => setSheet('status'))}
        {p.kind === 'brief' && row('Brief fields', () => action(p.onToggleFields))}
        {row('Share & export', () => setSheet('share'))}
        {row('Version history', () => action(p.onOpenVersionHistory))}
        {p.kind !== 'brief' && p.onMakeCutdown && row('Make a cutdown', () => action(p.onMakeCutdown), p.cutdownReason ?? undefined)}
        {p.remind && row('Remind client', () => action(p.remind!.onSelect), p.remind.enabled ? undefined : p.remind.line)}
        {row('Client email, approval date, checklist & recordings', () => action(() => p.onMobilePanel?.('admin-settings')))}
        {row(`${theme === 'dark' ? 'Light' : 'Dark'} theme`, toggle)}
        {row('Reset client view', () => action(p.onResetClientView), p.hasActiveSuggestion ? undefined : 'No open suggestion')}
        {p.onTakeDown && row('Take the page down', () => action(p.onTakeDown ?? undefined))}
      </>}
      {sheet === 'status' && <>
        <StatusToggle inline current={p.status} onChange={p.onStatusChange} labels={p.statusLabels ?? (s => STATUS_PILL_LABELS[s])} options={p.statusOptions} />
        <FeaturesPanel inline current={p.features} onChange={p.onFeaturesChange} onRotateSlug={p.onRotateSlug} />
      </>}
      {sheet === 'share' && <>
        {row(copied ? 'Copied' : 'Copy client link', () => { void Promise.resolve(p.onCopyShareUrl()).then(() => setCopied(true)); }, p.linkShareable ? undefined : 'Move past Draft to share')}
        {p.kind === 'brief' && p.onDownloadBriefPdf ? <>
          {row('Client PDF', () => action(() => p.onDownloadBriefPdf?.('client')))}
          {row('Full internal PDF', () => action(() => p.onDownloadBriefPdf?.('full')))}
        </> : row('Download PDF', () => action(p.onDownloadPdf))}
      </>}
    </MobileSheet>
  </>;
}
